import { createContainer, createHook, createStore } from 'react-sweet-state';
import type { Action } from 'react-sweet-state';

import {
	DEFAULT_MARKDOWN_VIEW_MODE,
	type MarkdownViewMode,
	readStoredMarkdownViewMode,
	writeStoredMarkdownViewMode,
} from '@confluence/content-markdown-mode/entry-points/markdownViewMode.ts';

type State = {
	view: MarkdownViewMode;
};

type Actions = {
	setView: (view: MarkdownViewMode) => Action<State>;
};

const initialState: State = {
	view: DEFAULT_MARKDOWN_VIEW_MODE,
};

const Store = createStore<State, Actions>({
	initialState,
	actions: {
		setView:
			(view) =>
			({ setState, getState }) => {
				if (getState().view === view) {
					return;
				}
				setState({ view });
				writeStoredMarkdownViewMode(view);
			},
	},
	name: 'markdown-mode-view',
});

/**
 * Container scope key. Mounted once per Live Doc by the live-doc editor with
 * `scope={contentId}` so the toggle and the editor body share the same
 * content-scoped state.
 *
 * On mount, `onInit` hydrates the initial `view` from the user's persisted
 * preference (`confluenceLocalStorageInstance`, user-namespaced and gated by
 * the `cc-markdown-mode` experiment). This makes the last view the user picked
 * the default the next time they open a markdown-mode page. Falls back to
 * `'wysiwyg'` when there is no stored value or the experiment is off.
 */
export type MarkdownModeViewContainerProps = {
	scope: string;
};

export const MarkdownModeViewContainer = createContainer<
	State,
	Actions,
	MarkdownModeViewContainerProps
>(Store, {
	onInit:
		() =>
		({ setState }) => {
			setState({ view: readStoredMarkdownViewMode() });
		},
});

export const useMarkdownModeView = createHook(Store);
